import { useCallback, useEffect, useRef, useState } from 'react';

interface UseTableSearchProps {
  onSearch: (searchTerm: string) => void;
}

const useTableSearch = ({ onSearch }: UseTableSearchProps) => {
  const [searchText, setSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const executeSearch = useCallback(() => {
    onSearch(searchText);
  }, [searchText, onSearch]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      executeSearch();
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setIsDropdownOpen(true);
  };

  const resetSearch = useCallback(() => {
    setSearchText('');
    onSearch('');
    setIsDropdownOpen(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [onSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return {
    searchText,
    isDropdownOpen,
    isFocused,
    searchInputRef,
    setIsFocused,
    handleSearchInputChange,
    handleKeyPress,
    executeSearch,
    resetSearch
  };
};

export default useTableSearch;